import { useEffect, useState } from 'react';
import type { ScenarioCompareRow } from '@integrate/contracts';
import { fmt, fmtMoney } from '@integrate/core';
import { api, ApiError } from '../api.js';

/*
  The scenario comparison, on screen at last.

  Until the `/scenario-compare` transport existed this pane was the analytics modal's one recorded
  absence: the store holds ONE design at a time, so the client cannot price a sibling scenario, and
  the tab was withheld rather than faked. The rows arrive already priced — the same engine calls,
  with the same design parameter, as the deck's compare slide — so the screen and the SteerCo pack
  cannot disagree about what an option costs.

  FETCHED ON EVERY MOUNT, deliberately. Panes unmount on a tab switch, so coming back re-prices
  against the designs as saved. That is also this pane's one honesty boundary, and it is stated in
  the caption: the rows describe the SERVER's designs. A bucket clicked a moment ago rides this
  tab's optimistic overlay, which this pane does not read; it reaches these rows when it is
  acknowledged, exactly as it reaches everyone else's screen.
*/
export function ComparePane({
  projectId,
  activeScenarioId,
  scenarioCount,
  metric,
  onMetric,
}: {
  projectId: string;
  /** Which row is the design on screen behind this modal — marked, like the slide's "this deck". */
  activeScenarioId: string;
  /**
   * How many scenarios the project has, live from the store. A fetch dependency, not display: a
   * scenario created or deleted by a colleague while this tab is open re-prices the table, so the
   * pane cannot show two options while the switcher beside it shows three. Renames and design
   * edits do not move the count — those land on the Refresh button or the next tab entry, and the
   * caption says whose numbers these are meanwhile.
   */
  scenarioCount: number;
  /** Owned by the modal, like every pane control: pane-local state dies with a tab switch. */
  metric: 'hc' | 'fte';
  onMetric(next: 'hc' | 'fte'): void;
}): JSX.Element {
  const [rows, setRows] = useState<ScenarioCompareRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped by the retry and refresh buttons; the effect depends on it, so both are one re-fetch.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    api
      .scenarioCompare(projectId)
      .then((response) => {
        if (!cancelled) setRows(response.rows);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : 'The comparison could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, scenarioCount, attempt]);

  if (error !== null) {
    return (
      <div data-testid="compare-pane">
        <p className="muted" data-testid="compare-error">
          {error}
        </p>
        <button onClick={() => setAttempt((n) => n + 1)}>Try again</button>
      </div>
    );
  }
  if (rows === null) {
    return (
      <p className="muted" data-testid="compare-loading">
        Pricing every scenario…
      </p>
    );
  }

  const number = metric === 'hc' ? (v: number) => fmt(Math.round(v)) : (v: number) => fmt(v, 1);

  return (
    <div data-testid="compare-pane" data-metric={metric}>
      <p className="muted small" style={{ marginTop: 0 }}>
        Every scenario priced over the same roster and assumptions, as saved on the server — an edit
        made a moment ago joins its row once it is acknowledged. Day 1 cost is raw annual salary;
        one-offs are severance + hiring + retention; stranded residual is the RemainCo run-rate
        after mitigation (loaded), per annum.
      </p>

      <div className="row" style={{ alignItems: 'center', gap: 12 }}>
        <span className="muted small">Headcount as</span>
        <div className="tabs" role="group" aria-label="Headcount metric">
          {(
            [
              { key: 'hc', label: 'Headcount' },
              { key: 'fte', label: 'FTE' },
            ] as const
          ).map((m) => (
            <button
              key={m.key}
              className={`toggle-btn${metric === m.key ? ' active' : ''}`}
              aria-pressed={metric === m.key}
              data-testid={`compare-metric-${m.key}`}
              onClick={() => onMetric(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <span className="spacer" />
        {/* Renames and design edits do not move the scenario count, so they need a hand-pulled door. */}
        <button data-testid="compare-refresh" onClick={() => setAttempt((n) => n + 1)}>
          Refresh
        </button>
      </div>

      <table className="data-table" data-testid="compare-table">
        <thead>
          <tr>
            <th>Scenario</th>
            <th className="num">Day 1 ({metric === 'hc' ? 'hc' : 'FTE'})</th>
            <th className="num">Day 2 ({metric === 'hc' ? 'hc' : 'FTE'})</th>
            <th className="num">Day 1 cost</th>
            <th className="num">One-off total</th>
            <th className="num">Stranded residual p.a.</th>
          </tr>
        </thead>
        <tbody data-testid="compare-rows" data-count={rows.length}>
          {rows.map((row) => {
            const active = row.id === activeScenarioId;
            const bridge = metric === 'hc' ? row.hc : row.fte;
            return (
              <tr key={row.id} data-testid={`compare-row-${row.id}`} data-active={active || undefined}>
                <td>
                  <strong>{row.name}</strong>
                  {active && (
                    /* The slide says "this deck"; the screen's equivalent claim. */
                    <span className="pill" data-testid={`compare-current-${row.id}`}>
                      on screen
                    </span>
                  )}
                  {row.isDefault && <span className="pill muted">default</span>}
                  {row.perimeterFrozen && (
                    <span className="pill" data-testid={`compare-frozen-${row.id}`}>
                      perimeter frozen
                    </span>
                  )}
                </td>
                <td className="num">{number(bridge.day1)}</td>
                <td className="num">{number(bridge.day2)}</td>
                <td className="num">{fmtMoney(row.costDay1)}</td>
                <td className="num">{fmtMoney(row.oneOffTotal)}</td>
                <td className="num">{fmtMoney(row.strandedResidual)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
